import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { preflightBundleImport } from '@/lib/api/generated/api';
import type { BundleImportPreview } from '@/lib/api/generated/model';

import { workflowKeys } from '../queryKeys';
import type { BundleAnswersRequest } from '../upload/bundleAnswers';
import { answersField, refusal } from './useBundleImport';

/** A stable id per chosen file: a `File` cannot be part of a query key. */
const fileIds = new WeakMap<File, number>();
let nextFileId = 0;

export function fileIdOf(file: File): number {
  let id = fileIds.get(file);
  if (id === undefined) {
    nextFileId += 1;
    id = nextFileId;
    fileIds.set(file, id);
  }
  return id;
}

interface Checked {
  workspaceId: string;
  fileId: number;
  preview: BundleImportPreview;
}

/**
 * What importing this bundle here would do, with these answers (spec 208;
 * service spec 163).
 *
 * A query, though the endpoint is a POST: the check writes nothing and is a
 * function of the workspace, the file and the answers, so it runs by itself
 * whenever they change, and only the response for the current ones is shown —
 * a slow answer to a question since changed can never replace a newer one.
 *
 * While the next check runs, the last review stays on screen (it would blank
 * and jump otherwise), but never one for another file or another workspace.
 * A refusal is an answer, so nothing is retried.
 */
export function useBundleCheck(
  workspaceId: string,
  file: File | null,
  answers: BundleAnswersRequest,
) {
  const fileId = file ? fileIdOf(file) : 0;
  const query = useQuery({
    queryKey: workflowKeys.bundleCheck(workspaceId, fileId, answers),
    queryFn: async (): Promise<Checked> => {
      if (!file) throw new Error('No bundle chosen.');
      const response = await preflightBundleImport(workspaceId, {
        file,
        answers: answersField(answers),
      });
      if (response.status === 200) {
        return { workspaceId, fileId, preview: response.data };
      }
      throw refusal(response.status, response.data);
    },
    enabled: file !== null,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
    gcTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const ours =
    query.data?.fileId === fileId && query.data.workspaceId === workspaceId;
  return {
    preview: ours ? query.data?.preview : undefined,
    /** A check is on its way, for these answers or since they changed. */
    checking: query.isFetching,
    /** The preview shown was made with exactly these answers. */
    current: ours && !query.isPlaceholderData && !query.isFetching,
    failure: query.error,
    retry: () => void query.refetch(),
  };
}
